<template>
  <Suspense>
    <template #default>
      <component 
        v-if="shouldLoad"
        :is="lazyComponent"
        :radius="radius"
        :locations="locations"
    />
    </template>
    <template #fallback>
      <p v-if="!shouldLoad">
        Please enable reCAPTCHA consent to load this map.
      </p>
    </template>
  </Suspense>
</template>

<script setup>
import { inject, ref, onMounted, onBeforeUnmount } from 'vue'

const loader = inject('GoogleMapLazyLoader')

const props = defineProps({
  radius: {
    type: Number,
    required: true
  },
  locations: { 
    type: [Object, Array, String],
    required: false,
    default: () => []
  }
})

const shouldLoad = ref(false)
const lazyComponent = ref(null)

const hasConsent = ref(false)
const inView = ref(false)

const loadComponent = async () => {
  try {
    const module = await loader()
    lazyComponent.value = module.default || module
    shouldLoad.value = true
  } catch (err) {
    console.error('Failed to load component:', err)
  }
}

const unloadComponent = () => {
  lazyComponent.value = null
  shouldLoad.value = false
}

const tryLoad = () => {
  if (hasConsent.value && inView.value && !shouldLoad.value) {
    loadComponent()
  }
  if ((!hasConsent.value || !inView.value) && shouldLoad.value) {
    unloadComponent()
  }
}

const checkConsent = () => {
  const saved = localStorage.getItem('consent_preferences')
  if (saved) {
    const prefs = JSON.parse(saved)
    hasConsent.value = prefs.recaptcha === true && prefs.maps === true
  } else {
    hasConsent.value = false
  }
  tryLoad()
}

const onConsentUpdated = () => {
  const updated = localStorage.getItem('consent_preferences')
  if (updated) {
    hasConsent.value = JSON.parse(updated).recaptcha === true
  } else {
    hasConsent.value = false
  }
  tryLoad()
}

onMounted(() => {
  checkConsent()

  // Intersection Observer for the heading
  const target = document.getElementById('areas-we-cover')
  if (!target) {
    inView.value = true // fallback: no heading, load immediately if consented
    tryLoad()
  } else {
    const observer = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            inView.value = true
            tryLoad()
            obs.disconnect()
          }
        })
      },
      { root: null, threshold: 0.1 }
    )
    observer.observe(target)
    onBeforeUnmount(() => {
      observer.disconnect()
    })
  }

  window.addEventListener('consent-updated', onConsentUpdated)
})

onBeforeUnmount(() => {
  window.removeEventListener('consent-updated', onConsentUpdated)
})
</script>
